import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Webhook } from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ErrorBoundary from './components/shared/ErrorBoundary';
import Layout from './components/Layout';
import Login from './components/Login';
import AuthCallback from './components/AuthCallback';
import SilentRenew from './components/SilentRenew';
import LogoutCallback from './components/LogoutCallback';
import Dashboard from './components/Dashboard';
import OrganizationSettings from './components/organization/OrganizationSettings';
import MembersList from './components/members/MembersList';
import ApiClients from './components/clients/ApiClients';
import ComingSoon from './components/shared/ComingSoon';
import AuditLog from './components/audit/AuditLog';
import CustomersList from './components/customers/CustomersList';
import CustomerDetailPage from './components/customers/CustomerDetailPage';
import FinancialsList from './components/financials/FinancialsList';
import FinancialDetailPage from './components/financials/FinancialDetailPage';
import DeveloperOverview from './components/developers/DeveloperOverview';
import EmbeddedAppGuide from './components/developers/EmbeddedAppGuide';
import OrdersList from './components/orders/OrdersList';
import PortfolioListPage from './components/portfolio/PortfolioListPage';
import PortfolioDetailPage from './components/portfolio/PortfolioDetailPage';
import { PriceFeedProvider } from './lib/price-feed/PriceFeedProvider';
import OrderDetailPage from './components/orders/OrderDetailPage';

// Webhook delivery is still stubbed server-side (no persistence, no real Kafka
// dispatch) — nav stays visible (marked "Soon") but the route resolves to a
// placeholder rather than the underlying (unready) feature.
const WebhooksComingSoon = () => (
  <ComingSoon
    icon={Webhook}
    title="Webhooks"
    description="Webhook delivery is being finalized and will be available shortly."
  />
);

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-50">
        <div className="animate-spin rounded-full h-7 w-7 border-2 border-accent-500 border-r-transparent" />
      </div>
    );
  }

  if (!loading && !isAuthenticated && user === null) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const withLayout = (Component) => (
  <ProtectedRoute>
    <Layout>
      <Component />
    </Layout>
  </ProtectedRoute>
);

const AppContent = () => (
  <Router>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/silent-renew" element={<SilentRenew />} />
      <Route path="/auth/logout-callback" element={<LogoutCallback />} />

      <Route path="/" element={withLayout(Dashboard)} />
      <Route path="/customers" element={withLayout(CustomersList)} />
      <Route path="/customers/:customerId" element={withLayout(CustomerDetailPage)} />
      <Route path="/orders" element={withLayout(OrdersList)} />
      <Route path="/orders/:orderId" element={withLayout(OrderDetailPage)} />
      <Route path="/trading" element={<Navigate to="/orders" replace />} />
      <Route path="/trading/*" element={<Navigate to="/orders" replace />} />
      <Route path="/portfolio" element={withLayout(PortfolioListPage)} />
      <Route path="/portfolio/:customerId" element={withLayout(PortfolioDetailPage)} />
      <Route path="/financials" element={withLayout(FinancialsList)} />
      <Route path="/financials/:id" element={withLayout(FinancialDetailPage)} />
      <Route path="/team" element={withLayout(MembersList)} />
      <Route path="/webhooks" element={withLayout(WebhooksComingSoon)} />
      <Route path="/settings" element={withLayout(OrganizationSettings)} />
      <Route path="/audit" element={withLayout(AuditLog)} />
      <Route path="/api-clients" element={withLayout(ApiClients)} />
      <Route path="/developers" element={withLayout(DeveloperOverview)} />
      <Route path="/developers/embed" element={withLayout(EmbeddedAppGuide)} />

      {/* Backwards-compat redirects */}
      <Route path="/organization" element={<Navigate to="/settings" replace />} />
      <Route path="/members" element={<Navigate to="/team" replace />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </Router>
);

const App = () => (
  <ErrorBoundary>
    <AuthProvider>
      <PriceFeedProvider>
        <AppContent />
      </PriceFeedProvider>
    </AuthProvider>
  </ErrorBoundary>
);

export default App;
